/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FormikProps } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { FormControl, FormHelperText, InputLabel, OutlinedInput, Typography } from '@mui/material';

import { KubernetesIdentifier, Language, Languages } from 'shared';

import { useEventCallback } from '../../../components/hooks/useEventCallback';
import { ArrowRoundedIcon } from '../../../components/svg-icons/ArrowRoundedIcon';
import { ThumbnailSelector, ThumbnailSelectorType } from '../../../components/ThumbnailSelector/ThumbnailSelector';
import { HUAutocomplete } from '../../../elements/HUAutocomplete/HUAutocomplete';
import { destructRenderInputParams } from '../../../elements/HUTextInput/utils';
import { Namespaces } from '../../../i18n';
import { useFormStyles } from '../../../styles/form';
import { ThumbnailOption } from '../../../types/thumbnail/ThumbnailOption';
import { readFile } from '../../../utils/canvasUtils';
import { useCreateOrganizationOverlayFormStyles } from '../CreateOrganizationOverlay.styles';
import {
  CreateOrganizationApiValidationErrors,
  CreateOrganizationValues,
  ResetApiErrorForField,
} from '../CreateOrganizationOverlay.types';

type CreateOrganizationStep1Props = FormikProps<CreateOrganizationValues> & {
  resetApiError: ResetApiErrorForField;
  apiErrors: CreateOrganizationApiValidationErrors;
  disabled?: boolean;
};

export function CreateOrganizationStep1(props: CreateOrganizationStep1Props) {
  const {
    touched,
    values,
    errors,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    resetApiError,
    apiErrors,
    setValues,
    disabled,
  } = props;
  const formClasses = useFormStyles();
  const classes = useCreateOrganizationOverlayFormStyles();
  const { t } = useTranslation([Namespaces.CreateOrganization, Namespaces.Common]);

  const { thumbnailsOptions } = values;

  const onChange = (name: keyof CreateOrganizationApiValidationErrors, event: React.ChangeEvent) => {
    handleChange(event);
    resetApiError(name);
  };

  useEffect(() => {
    if (values.thumbnail === null && thumbnailsOptions[0]) {
      void setFieldValue('thumbnail', thumbnailsOptions[0]);
    }
  }, [setFieldValue, thumbnailsOptions, values.thumbnail]);

  const onImageUpload = useEventCallback(async (thumbnail: Blob) => {
    const newThumbnailOption: ThumbnailOption = { type: 'base64', base64: await readFile(thumbnail) };
    void setValues({
      ...values,
      thumbnail: newThumbnailOption,
    });
  });

  const nameError = errors.name || apiErrors.name;
  const hasNameError = touched.name && !!nameError;

  const descriptionError = errors.description || apiErrors.description;
  const hasDescriptionError = touched.description && !!descriptionError;

  const companyNameError = errors.companyName || apiErrors.companyName;
  const hasCompanyNameError = touched.companyName && !!companyNameError;

  const emailError = errors.email || apiErrors.email;
  const hasEmailError = touched.email && !!emailError;

  const languageError = errors.language || apiErrors.language;
  const hasLanguageError = touched.language && !!languageError;

  const vatNumberError = errors.vatNumber || apiErrors.vatNumber;
  const hasVatNumberError = touched.language && !!vatNumberError;

  return (
    <>
      {!!thumbnailsOptions.length && (
        <FormControl className={cx(formClasses.formControl, formClasses.widthFull)} sx={{ pb: '5px' }}>
          <ThumbnailSelector
            onItemClick={value => setFieldValue('thumbnail', value)}
            options={thumbnailsOptions}
            type={ThumbnailSelectorType.organization}
            value={values.thumbnail}
            allowUploadImage={true}
            onImageUpload={onImageUpload}
          />
        </FormControl>
      )}
      <Typography className={classes.subtitle} variant="h4" component="h4">
        {t('input_groups.info')}
      </Typography>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium, classes.helperText)}
        error={hasNameError}
        variant="outlined"
      >
        <InputLabel htmlFor="name">{t('input_labels.name')}*</InputLabel>
        <OutlinedInput
          id="name"
          name="name"
          value={values.name}
          onChange={onChange.bind(null, 'name')}
          label={t('input_labels.name')}
          onBlur={handleBlur}
          data-cy="create-organization-form-name-input"
          disabled={disabled}
        />
        {hasNameError && (
          <FormHelperText data-cy="create-organization-form-name-error">
            {t(`validation.name.${nameError}`, {
              minLength: KubernetesIdentifier.MinLength,
              maxLength: KubernetesIdentifier.MaxLength,
            })}
          </FormHelperText>
        )}
        <FormHelperText sx={{ color: 'rgba(0,0,0,0.6) !important' }}>{t('input_hints.name')}</FormHelperText>
      </FormControl>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium, classes.helperText)}
        error={hasDescriptionError}
        variant="outlined"
      >
        <InputLabel htmlFor="description">{t('input_labels.description')}</InputLabel>
        <OutlinedInput
          id="description"
          name="description"
          value={values.description}
          onChange={onChange.bind(null, 'description')}
          label={t('input_labels.description')}
          fullWidth
          multiline
          onBlur={handleBlur}
          disabled={disabled}
          data-cy="create-organization-form-description-input"
        />
        {hasDescriptionError && <FormHelperText>{t(`validation.description.${descriptionError}`)}</FormHelperText>}
        <FormHelperText sx={{ color: 'rgba(0,0,0,0.6) !important' }}>{t('input_hints.description')}</FormHelperText>
      </FormControl>
      <Typography className={classes.subtitle} variant="h4" component="h4">
        {t('input_groups.contact')}
      </Typography>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium, classes.helperText)}
        error={hasEmailError}
        variant="outlined"
      >
        <InputLabel htmlFor="email">{t('input_labels.email')}</InputLabel>
        <OutlinedInput
          id="email"
          name="email"
          value={values.email}
          onChange={onChange.bind(null, 'email')}
          label={t('input_labels.email')}
          onBlur={handleBlur}
          disabled={disabled}
          data-cy="create-organization-form-email-input"
        />
        {hasEmailError && <FormHelperText>{t(`validation.email.${emailError}`)}</FormHelperText>}
        <FormHelperText sx={{ color: 'rgba(0,0,0,0.6) !important' }}>{t('input_hints.email')}</FormHelperText>
      </FormControl>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium, classes.helperText)}
        error={hasLanguageError}
        variant="outlined"
      >
        <InputLabel htmlFor="language">{t('input_labels.language')}*</InputLabel>
        <HUAutocomplete
          popupIcon={<ArrowRoundedIcon />}
          renderInput={renderParams => (
            <OutlinedInput
              {...destructRenderInputParams(renderParams)}
              label={t('input_labels.language')}
              disabled={disabled}
              data-cy="create-organization-form-language-input"
            />
          )}
          options={Languages}
          disableClearable
          value={values.language ?? undefined}
          onChange={(_e: any, value: Language | null) => {
            if (value !== null) {
              void setFieldValue('language', value, true);
              void setFieldTouched('language', true);
              resetApiError('language');
            }
          }}
          onBlur={() => void setFieldTouched('language', true)}
          data-cy="create-organization-form-language-input"
          disabled={disabled}
        />
        {hasLanguageError && <FormHelperText>{t(`validation.language.${languageError}`)}</FormHelperText>}
        <FormHelperText sx={{ color: 'rgba(0,0,0,0.6) !important' }}>{t('input_hints.language')}</FormHelperText>
      </FormControl>
      <Typography className={classes.subtitle} variant="h4" component="h4">
        {t('input_groups.tax_info')}
      </Typography>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium, classes.helperText)}
        error={hasCompanyNameError}
        variant="outlined"
        disabled={disabled}
      >
        <InputLabel htmlFor="companyName">{t('input_labels.company_name')}*</InputLabel>
        <OutlinedInput
          id="companyName"
          name="companyName"
          value={values.companyName}
          onChange={onChange.bind(null, 'companyName')}
          label={t('input_labels.company_name')}
          onBlur={handleBlur}
          data-cy="create-organization-form-company-name-input"
        />
        {hasCompanyNameError && (
          <FormHelperText data-cy="create-organization-form-company-name-error">
            {t(`validation.companyName.${companyNameError}`)}
          </FormHelperText>
        )}
        <FormHelperText sx={{ color: 'rgba(0,0,0,0.6) !important' }}>{t('input_hints.company_name')}</FormHelperText>
      </FormControl>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium, classes.helperText)}
        error={hasVatNumberError}
        variant="outlined"
      >
        <InputLabel htmlFor="vatNumber">{t('input_labels.vat_number')}</InputLabel>
        <OutlinedInput
          id="vatNumber"
          name="vatNumber"
          value={values.vatNumber}
          onChange={onChange.bind(null, 'vatNumber')}
          label={t('input_labels.vat_number')}
          onBlur={handleBlur}
          disabled={disabled}
          data-cy="create-organization-form-vat-number-input"
        />
        {hasVatNumberError && <FormHelperText>{t(`validation.vatNumber.${vatNumberError}`)}</FormHelperText>}
        <FormHelperText sx={{ color: 'rgba(0,0,0,0.6) !important' }}>{t('input_hints.vat_number')}</FormHelperText>
      </FormControl>
    </>
  );
}
